import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';
import Navigation from './components/Navigation';
import NewsButton from './components/news/NewsButton';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import OfflineIndicator from './components/pwa/OfflineIndicator';
import PushNotificationManager from './components/pwa/PushNotificationManager';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import CommunityForum from './pages/CommunityForum';
import SafetyGuide from './pages/SafetyGuide';
import Resources from './pages/Resources';
import { initializeOfflineStorage } from './utils/offlineStorage';

function App() {
  useEffect(() => {
    // Initialize PWA features
    const initializePWA = async () => {
      // Register service worker
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/sw.js');
          console.log('Service Worker registered successfully:', registration);
          
          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New content is available, prompt user to refresh
                  if (confirm('New version available! Refresh to update?')) {
                    window.location.reload();
                  }
                }
              });
            }
          });
        } catch (error) {
          console.error('Service Worker registration failed:', error);
        }
      }

      // Initialize offline storage
      await initializeOfflineStorage();

      // Set up periodic sync for online users
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        navigator.serviceWorker.ready.then((registration) => {
          // Register for background sync
          return registration.sync.register('disaster-data');
        });
      }
    };

    initializePWA();

    // Handle app lifecycle events
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // App became visible, sync data if online
        if (navigator.onLine) {
          // Trigger data sync
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.controller?.postMessage({
              type: 'SYNC_DATA'
            });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <Router>
      <div className="bg-dark-900 text-white min-h-screen">
        <Navigation />
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/community" element={<CommunityForum />} />
            <Route path="/safety" element={<SafetyGuide />} />
            <Route path="/resources" element={<Resources />} />
          </Routes>
        </AnimatePresence>
        
        {/* Global News Button - Available on all pages */}
        <NewsButton />
        
        {/* PWA Components */}
        <PWAInstallPrompt />
        <OfflineIndicator />
        <PushNotificationManager />
        
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#1e293b',
              color: '#ffffff',
              border: '1px solid #334155',
            },
          }}
        />
      </div>
    </Router>
  );
}

export default App;